/**
 * klubb-club-state — global "selected club" state for the Klubb.no theme.
 *
 * Hydrates from a cookie at boot, exposes a small API on window.klubb,
 * broadcasts a 'change' event when the active club changes, and dispatches
 * document-level CustomEvents for picker open/close so any listener (the
 * picker modal, analytics, etc.) can react.
 *
 * Source of truth for selected club:
 *   1. Cookie 'klubb_active' (slug)
 *   2. JSON in <script id="klubb-clubs-data" type="application/json">
 *
 * Cookie semantics: 365 days, SameSite=Lax, Secure.
 */

(function (root) {
  'use strict';

  const COOKIE_NAME = 'klubb_active';
  const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

  // --- Cookie helpers ---
  function readCookie(name) {
    const raw = document.cookie || '';
    const parts = raw.split(';');
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i].trim();
      const eq = p.indexOf('=');
      const key = eq > -1 ? p.substr(0, eq) : p;
      if (key === name) {
        return eq > -1 ? decodeURIComponent(p.substr(eq + 1)) : '';
      }
    }
    return null;
  }
  function writeCookie(name, value) {
    const isHttps = (typeof location !== 'undefined') && location.protocol === 'https:';
    const secure = isHttps ? '; Secure' : '';
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; path=/' +
      '; max-age=' + COOKIE_MAX_AGE_SECONDS +
      '; SameSite=Lax' +
      secure;
  }
  function deleteCookie(name) {
    document.cookie = name + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }

  // --- Load club data injected by Liquid ---
  function loadClubsData() {
    const el = document.getElementById('klubb-clubs-data');
    if (!el) return [];
    try {
      const data = JSON.parse(el.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (e) {
      // Malformed — degrade silently
      return [];
    }
  }

  function findClubBySlug(clubs, slug) {
    if (!slug) return null;
    for (let i = 0; i < clubs.length; i++) {
      if (clubs[i] && clubs[i].slug === slug) return clubs[i];
    }
    return null;
  }

  // --- Event bus (small, no dependencies) ---
  function createBus() {
    const handlers = {};
    return {
      on: function (event, handler) {
        if (!handlers[event]) handlers[event] = [];
        handlers[event].push(handler);
      },
      off: function (event, handler) {
        if (!handlers[event]) return;
        const idx = handlers[event].indexOf(handler);
        if (idx > -1) handlers[event].splice(idx, 1);
      },
      emit: function (event, payload) {
        const list = handlers[event] || [];
        for (let i = 0; i < list.length; i++) {
          try {
            list[i](payload);
          } catch (err) {
            // Don't let one handler block others; log for dev visibility.
            if (root && root.console && root.console.error) {
              root.console.error('[klubb] handler threw:', err);
            }
          }
        }
      },
    };
  }

  // --- Build the public API ---
  const clubs = loadClubsData();
  const bus = createBus();

  let activeClub = null;
  const cookieSlug = readCookie(COOKIE_NAME);
  if (cookieSlug) {
    const found = findClubBySlug(clubs, cookieSlug);
    if (found) {
      activeClub = found;
    } else {
      // Stale cookie — clear it
      deleteCookie(COOKIE_NAME);
    }
  }

  // The club this page was drawn for. Another tab can switch the club (the
  // cookie is shared); pageClubChanged() tells the page it is out of date.
  let pageSlug = activeClub ? activeClub.slug : null;

  // Another tab may have changed or cleared the cookie since boot (18.10.2026):
  // re-read it on every access so an add-to-cart from an old tab stamps the
  // club the cart actually has. A change is broadcast once, after the caller.
  function syncFromCookie() {
    const slug = readCookie(COOKIE_NAME) || null;
    const current = activeClub ? activeClub.slug : null;
    if (slug === current) return;
    const next = slug ? findClubBySlug(clubs, slug) : null;
    if (slug && !next) return; // unknown slug: leave the state alone
    activeClub = next;
    Promise.resolve().then(function () { bus.emit('change', next); });
  }

  const api = {
    get activeClub() { syncFromCookie(); return activeClub; },

    pageClubChanged: function () {
      syncFromCookie();
      return (activeClub ? activeClub.slug : null) !== pageSlug;
    },

    setActiveClub: function (slug) {
      const next = findClubBySlug(clubs, slug);
      if (!next) return; // Unknown slug: no-op (logged in dev later if needed)
      const same = activeClub && activeClub.slug === next.slug;
      activeClub = next;
      pageSlug = next.slug;
      writeCookie(COOKIE_NAME, next.slug);
      if (!same) bus.emit('change', next);
    },

    clearActiveClub: function () {
      const had = activeClub !== null;
      activeClub = null;
      pageSlug = null;
      deleteCookie(COOKIE_NAME);
      if (had) bus.emit('change', null);
    },

    openPicker: function () {
      document.dispatchEvent(new CustomEvent('klubb:picker:open'));
    },

    closePicker: function () {
      document.dispatchEvent(new CustomEvent('klubb:picker:close'));
    },

    on: bus.on,
    // Kept for public-API symmetry with on(); currently only exercised by tests.
    off: bus.off,
  };

  root.klubb = api;

  // Coming back to a tab whose page was drawn for another club than the one
  // now in the cookie (switched in another tab): redraw it.
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && api.pageClubChanged() && root.location) {
        root.location.reload();
      }
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
